"use client";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import Button from "../atoms/Button";
import { useJobStore } from "../../store/jobStore";

export default function BannerJobSearch() {
    const location = useLocation();
    const lang = location.pathname.startsWith("/id") ? "id" : "en";

    const {
        jobTypes,
        degrees,
        experienceLevels,
        industries,
        workingTypes,
        countries,
        states,
        cities,
        fetchFilters,
        fetchStates,
        fetchCities,
    } = useJobFilterStore();

    const { setFilters } = useJobStore();

    const [searchJob, setSearchJob] = useState("");
    const [countryId, setCountryId] = useState("");
    const [stateId, setStateId] = useState("");
    const [cityId, setCityId] = useState("");

    const [jobType, setJobType] = useState("");
    const [degree, setDegree] = useState("");
    const [experience, setExperience] = useState("");
    const [industry, setIndustry] = useState("");
    const [workingType, setWorkingType] = useState("");
    const [showFilter, setShowFilter] = useState(false);

    const handleCountryChange = async (value: string) => {
        setCountryId(value);
        setStateId("");
        setCityId("");

        if (value) {
            await fetchStates(Number(value), lang);
        }
    };


    const handleStateChange = async (value: string) => {
        setStateId(value);
        setCityId("");

        if (value) {
            await fetchCities(Number(value), lang);
        }
    };



    useEffect(() => {
        fetchFilters(lang);
    }, [lang, fetchFilters]);

    const handleSearch = () => {
        setFilters({
            keyword: searchJob,
            country_id: countryId || undefined,
            state_id: stateId || undefined,
            city_id: cityId || undefined,
            industry_id: industry || undefined,
            job_type_id: jobType || undefined,
            working_type_id: workingType || undefined,
            degree_id: degree || undefined,
            experience_level_id: experience || undefined,
        });
    };


    return (
        <div className="flex flex-col items-center justify-center text-center h-full">
            <h2
                className="text-3xl lg:text-5xl text-white mb-3 font-normal hidden md:block"
                style={{ textShadow: "0 3px 10px rgba(0,0,0,0.6)" }}
            >
                {lang === "id" ? "Temukan posisi yang tepat" : "Find the right position"}
            </h2>
            <p
                className="text-lg lg:text-xl text-white/90 mb-2 hidden md:block"
                style={{ textShadow: "0 2px 6px rgba(0,0,0,0.5)" }}
            >
                {lang === "id"
                    ? "Raih kesempatanmu hanya dengan satu klik"
                    : "Take your opportunity with one click"}
            </p>

            {/* Desktop */}
            <div className="backdrop-blur-md hidden lg:flex bg-white/20 rounded-2xl shadow-lg p-4 flex-col gap-3 w-full max-w-7xl mx-auto">
                {/* Baris 1 */}
                <div className="flex flex-wrap md:flex-nowrap gap-3 w-full">
                    {/* Search Job */}
                    <div className="flex items-center bg-white rounded-lg px-4 py-2 flex-1">
                        <Search className="h-5 w-5 text-gray-400 mr-2" />
                        <input
                            type="text"
                            placeholder={lang === "id" ? "Cari Pekerjaan..." : "Search Job..."}
                            value={searchJob}
                            onChange={(e) => setSearchJob(e.target.value)}
                            className="w-full bg-transparent outline-none text-gray-700"
                        />
                    </div>

                    {/* Location */}
                    <div className="flex items-center bg-white rounded-lg px-4 py-2 flex-1">
                        <select
                            value={countryId}
                            onChange={(e) => handleCountryChange(e.target.value)}
                            className="bg-white rounded-lg text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                        >
                            <option value="">
                                {lang === "id" ? "Negara" : "Country"}
                            </option>
                            {countries.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                        <select
                            value={stateId}
                            onChange={(e) => handleStateChange(e.target.value)}
                            className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            disabled={!countryId}
                        >
                            <option value="">
                                {lang === "id" ? "Provinsi / State" : "State"}
                            </option>
                            {states.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>
                        <select
                            value={cityId}
                            onChange={(e) => setCityId(e.target.value)}
                            className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            disabled={!stateId}
                        >
                            <option value="">
                                {lang === "id" ? "Kota" : "City"}
                            </option>
                            {cities.map((item) => (
                                <option key={item.id} value={item.id}>
                                    {item.name}
                                </option>
                            ))}
                        </select>

                    </div>


                    <select
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1"
                    >
                        <option value="">
                            {lang === "id" ? "Industri" : "Industry"}
                        </option>
                        {industries.map((item) => (
                            <option key={item.name} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Baris 2 */}
                <div className="flex flex-wrap md:flex-nowrap gap-3">
                    <select
                        value={jobType}
                        onChange={(e) => setJobType(e.target.value)}
                        className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1"
                    >
                        <option value="">
                            {lang === "id" ? "Jenis Pekerjaan" : "Job Type"}
                        </option>
                        {jobTypes.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                    <select
                        value={workingType}
                        onChange={(e) => setWorkingType(e.target.value)}
                        className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1"
                    >
                        <option value="">
                            {lang === "id" ? "Tipe Pekerjaan" : "Working Type"}
                        </option>
                        {workingTypes.map((item) => (
                            <option key={item.name} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>

                    <select
                        value={degree}
                        onChange={(e) => setDegree(e.target.value)}
                        className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1"
                    >
                        <option value="">
                            {lang === "id" ? "Pendidikan" : "Degree"}
                        </option>
                        {degrees.map((item) => (
                            <option key={item.name} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>

                    <select
                        value={experience}
                        onChange={(e) => setExperience(e.target.value)}
                        className="
    bg-white rounded-lg px-4 py-2 text-primary outline-none max-w-56
    truncate
  "
                    >
                        <option value="">
                            {lang === "id" ? "Pengalaman" : "Experience"}
                        </option>
                        {experienceLevels.map((item) => (
                            <option key={item.name} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    onClick={handleSearch}
                    className="bg-primary hover:bg-blue-800 text-white font-semibold rounded-lg px-8 py-2 transition-colors duration-200"
                >
                    {lang === "id" ? "Cari" : "Search"}
                </button>
            </div>

            <div className="backdrop-blur-md lg:hidden bg-white/20 rounded-2xl shadow-lg p-3 flex flex-col gap-3 w-full max-w-6xl mx-auto">

                {/* SEARCH INPUT (TETAP DI LUAR) */}
                <div className="flex items-center bg-white rounded-lg px-4 py-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-gray-400 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search Job..."
                        value={searchJob}
                        onChange={(e) => setSearchJob(e.target.value)}
                        className="w-full bg-transparent outline-none text-gray-700"
                    />
                </div>

                {/* BUTTON ROW */}
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowFilter(true)}
                        className="flex-1 bg-white text-primary font-semibold rounded-lg px-4 py-2"
                    >
                        Filter
                    </button>

                    <button
                        onClick={handleSearch}
                        className="flex-1 bg-primary text-white font-semibold rounded-lg px-4 py-2"
                    >
                        Search
                    </button>
                </div>
            </div>

            {showFilter && (
                <>
                    {/* Overlay */}
                    <div
                        className="fixed inset-0 bg-black/40 z-50"
                        onClick={() => setShowFilter(false)}
                    />

                    {/* Bottom Sheet */}
                    <div className="fixed bottom-0 left-0 w-full bg-white rounded-t-3xl p-6 z-50 max-h-[80vh] overflow-y-auto animate-slideUp">

                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold">
                                {lang === "id" ? "Filter Pekerjaan" : "Job Filter"}
                            </h2>
                            <button className="text-black" onClick={() => setShowFilter(false)}>
                                ✕
                            </button>
                        </div>


                        <div className="flex flex-wrap md:flex-nowrap gap-3 flex-col">

                            <select
                                value={countryId}
                                onChange={(e) => handleCountryChange(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            >
                                <option value="">
                                    {lang === "id" ? "Negara" : "Country"}
                                </option>
                                {countries.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={stateId}
                                onChange={(e) => handleStateChange(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                                disabled={!countryId}
                            >
                                <option value="">
                                    {lang === "id" ? "Provinsi / State" : "State"}
                                </option>
                                {states.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={cityId}
                                onChange={(e) => setCityId(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                                disabled={!stateId}
                            >
                                <option value="">
                                    {lang === "id" ? "Kota" : "City"}
                                </option>
                                {cities.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={jobType}
                                onChange={(e) => setJobType(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            >
                                <option value="">
                                    {lang === "id" ? "Jenis Pekerjaan" : "Job Type"}
                                </option>
                                {jobTypes.map((item) => (
                                    <option key={item.name} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={workingType}
                                onChange={(e) => setWorkingType(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            >
                                <option value="">
                                    {lang === "id" ? "Tipe Pekerjaan" : "Working Type"}
                                </option>
                                {workingTypes.map((item) => (
                                    <option key={item.name} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={degree}
                                onChange={(e) => setDegree(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            >
                                <option value="">
                                    {lang === "id" ? "Pendidikan" : "Degree"}
                                </option>
                                {degrees.map((item) => (
                                    <option key={item.name} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={experience}
                                onChange={(e) => setExperience(e.target.value)}
                                className="
    bg-white rounded-lg px-4 py-2 text-primary outline-none
    
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap
  "
                                style={{ width: "-webkit-fill-available" }}
                            >
                                <option value="">
                                    {lang === "id" ? "Pengalaman" : "Experience"}
                                </option>
                                {experienceLevels.map((item) => (
                                    <option key={item.name} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                className="bg-white rounded-lg px-4 py-2 text-primary outline-none flex-1 
    w-full
    min-w-0
    overflow-hidden
    text-ellipsis
    whitespace-nowrap"
                            >
                                <option value="">
                                    {lang === "id" ? "Industri" : "Industry"}
                                </option>
                                {industries.map((item) => (
                                    <option key={item.name} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => {
                                    handleSearch();
                                    setShowFilter(false);
                                }}
                                className="bg-primary hover:bg-blue-800 text-white font-semibold rounded-lg px-8 py-2 transition-colors duration-200 w-full"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
}
